import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Table } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AmountInput } from "@/modules/amount-input";
import CategorySelect from "../TransactionView/CategorySelect";
import VatCategorySelect from "../TransactionView/VatCategorySelect";
import { EXTERNAL_TRANSACTION_CATEGORIES } from "../../../utils";
import { RuleSplitLineForm } from "./useRuleForm";

const toCents = (euros: number) => Math.round(euros * 100);

const RuleSplitView = ({
  splitLines,
  triedToSubmit,
  transactionAmount,
  onChange,
  onAdd,
  onDelete,
}: {
  splitLines: RuleSplitLineForm[];
  triedToSubmit: boolean;
  transactionAmount?: number;
  onChange: (line: RuleSplitLineForm, index: number) => void;
  onAdd: () => void;
  onDelete: (index: number) => void;
}) => {
  const { t } = useTranslation("Rules");
  const columns = useMemo(
    () => [
      {
        title: t("split.amount"),
        key: "amount",
        width: 150,
        render: (record: RuleSplitLineForm, _: unknown, index: number) => (
          <AmountInput
            value={record.amount ? record.amount / 100 : undefined}
            onChange={(value) => {
              if (value !== null) {
                onChange({ ...record, amount: toCents(value) }, index);
              }
            }}
            addonAfter="€"
            allowNegative
            status={triedToSubmit && !record.amount ? "error" : undefined}
            style={{ width: "100%" }}
          />
        ),
      },
      {
        title: t("split.category"),
        key: "categoryCode",
        width: 340,
        render: (record: RuleSplitLineForm, _: unknown, index: number) => (
          <CategorySelect
            id={`rule-split-category-${index}`}
            value={record.categoryCode || undefined}
            transactionAmount={transactionAmount ?? -1}
            invalid={triedToSubmit && !record.categoryCode}
            onChangeHandler={(categoryCode: string) =>
              onChange({ ...record, categoryCode }, index)
            }
            style={{ width: "100%", maxWidth: "320px" }}
            disabledOptions={EXTERNAL_TRANSACTION_CATEGORIES}
          />
        ),
      },
      {
        title: t("split.vatCategory"),
        key: "vatCategoryCode",
        width: 325,
        render: (record: RuleSplitLineForm, _: unknown, index: number) => (
          <VatCategorySelect
            id={`rule-split-vat-${index}`}
            value={record.vatCategoryCode || undefined}
            invalid={triedToSubmit && !record.vatCategoryCode}
            onChangeHandler={(vatCategoryCode: string | null) =>
              onChange(
                { ...record, vatCategoryCode: vatCategoryCode ?? "" },
                index
              )
            }
            updateValueOnCategoryChange
            kontaxCategory={record.categoryCode}
            style={{ width: "100%", maxWidth: "305px" }}
          />
        ),
      },
      ...(splitLines.length > 2
        ? [
            {
              title: "",
              key: "delete",
              width: 56,
              render: (_: unknown, __: unknown, index: number) => (
                <Button
                  shape="circle"
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(index)}
                />
              ),
            },
          ]
        : []),
    ],
    [splitLines.length, triedToSubmit, transactionAmount, onChange, onDelete, t]
  );

  const data = splitLines.map((line, index) => ({ key: index, ...line }));

  return (
    <div>
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        tableLayout="fixed"
        size="middle"
      />
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}
      >
        <Button
          icon={<PlusOutlined />}
          style={{
            display: "flex",
            alignItems: "center",
            border: "1px dashed #D9D9D9",
          }}
          onClick={onAdd}
          data-test="ruleSplit.addButton"
        >
          {t("split.add")}
        </Button>
      </div>
    </div>
  );
};

export default RuleSplitView;
